/**
 * SPIKE (RFC T1+T2) — Structured Response Blocks: schema Zod.
 *
 * Contrato: o backend envia, no evento `done` do SSE, `blocks: Block[]` + `blocks_version`.
 * Cada block é um objeto discriminado por `type`. Validamos com um discriminated union;
 * um block que falha na validação (ou de `type` desconhecido) é DESCARTADO silenciosamente,
 * e o front cai no texto (`Message.content`). Isso é o que torna o contrato extensível sem
 * quebrar clientes antigos (RFC §2.4).
 *
 * WRINKLE zod vs zod/v3: o pacote `zod@3.25.x` expõe as duas APIs. Usamos `zod/v3` (a API
 * clássica) para bater com os schemas existentes do repo (tasks/form/schema.ts, use-form.ts).
 * NÃO misturar `from "zod"` (API v4) e `from "zod/v3"` no mesmo grafo de tipos — os dois `z`
 * não são type-compatíveis.
 */
import { z } from "zod/v3";

import type { ChartSpec } from "../types";
import { trackDroppedBlock } from "../utils/blocks-telemetry";

// ── chart block (T2) ──────────────────────────────────────────────────────────
// Mapeia para o ChartSpec já existente (types/index.ts) para reusar <ChartRender/>.
const chartTypeSchema = z.enum(["bar", "line", "pie", "doughnut"]);

const seriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number().nullable()),
  unit: z.string().optional(),
});

export const chartBlockSchema = z.object({
  type: z.literal("chart"),
  id: z.string(),
  chart_type: chartTypeSchema.default("bar"),
  title: z.string().optional(),
  x: z.array(z.union([z.string(), z.number(), z.null()])),
  series: z.array(seriesSchema),
  options: z
    .object({
      stacked: z.boolean().optional(),
      horizontal: z.boolean().optional(),
    })
    .optional(),
  note: z.string().optional(),
  fallback_text: z.string().optional(),
});

export type ChartBlock = z.infer<typeof chartBlockSchema>;

// ── chips block (T3) ────────────────────────────────────────────────────────────
// Converte o campo legado `suggestions`. Mesma shape do `Suggestion` (text/action/category).
const chipItemSchema = z.object({
  text: z.string(),
  action: z.string().optional(),
  category: z.string().optional(),
});

export const chipsBlockSchema = z.object({
  type: z.literal("chips"),
  id: z.string(),
  items: z.array(chipItemSchema),
  fallback_text: z.string().optional(),
});

export type ChipsBlock = z.infer<typeof chipsBlockSchema>;

// ── expandable_list block (T3) ──────────────────────────────────────────────────
// Converte `response_expandable`/`response_added_list` num único block com `variant`.
export const expandableListBlockSchema = z.object({
  type: z.literal("expandable_list"),
  id: z.string(),
  variant: z.enum(["added", "error", "info"]).default("info"),
  label: z.string().optional(),
  items: z.array(z.string()),
  fallback_text: z.string().optional(),
});

export type ExpandableListBlock = z.infer<typeof expandableListBlockSchema>;

// ── tone (T4) ───────────────────────────────────────────────────────────────────
// `tone` é regra de negócio decidida no backend (positivo=acima da média=ruim=vermelho).
// O front só mapeia tone -> cor, nunca decide o sinal (RFC §3).
const toneSchema = z.enum(["positive", "negative", "neutral"]);

// ── kpi_group block (T4 — Figma Estratégia) ─────────────────────────────────────
const kpiItemSchema = z.object({
  caption: z.string(),
  value: z.string(),
  variant: z.enum(["metric", "identity"]).default("metric"),
  tone: toneSchema.optional(),
  hint: z.string().optional(),
  delta: z
    .object({
      value: z.string(),
      direction: z.enum(["up", "down", "flat"]),
    })
    .optional(),
});

export const kpiGroupBlockSchema = z.object({
  type: z.literal("kpi_group"),
  id: z.string(),
  items: z.array(kpiItemSchema),
  fallback_text: z.string().optional(),
});

export type KpiGroupBlock = z.infer<typeof kpiGroupBlockSchema>;

// ── table block (T4 — Figma Estratégia) ─────────────────────────────────────────
// Uma célula é `string` (texto puro) OU objeto com text/subtext/text_tone/badge.
// text_tone colore o texto (coluna R$/m²); badge renderiza pill (coluna Desvio).
const tableCellSchema = z.union([
  z.string(),
  z.object({
    text: z.string(),
    subtext: z.string().optional(),
    text_tone: toneSchema.optional(),
    badge: toneSchema.optional(),
  }),
]);

const tableColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  align: z.enum(["left", "right", "center"]).optional(),
  // Largura fixa da coluna (px numérico ou string CSS, ex.: "100%") para alinhar
  // colunas numéricas entre tabelas diferentes.
  width: z.union([z.string(), z.number()]).optional(),
});

export const tableBlockSchema = z.object({
  type: z.literal("table"),
  id: z.string(),
  title: z.string().optional(),
  columns: z.array(tableColumnSchema),
  rows: z.array(z.record(tableCellSchema)),
  fallback_text: z.string().optional(),
});

export type TableBlock = z.infer<typeof tableBlockSchema>;
export type TableCell = z.infer<typeof tableCellSchema>;
export type Tone = z.infer<typeof toneSchema>;

// ── form block (T5 — Figma Operação, abrir Ordem de Serviço) ─────────────────────
// Botão auxiliar "Melhorar com IA" num campo de texto: dispara um block_action leve
// (intent=assist) sem submeter o form (RFC §12.3).
const assistActionSchema = z.object({
  label: z.string(),
  action: z.string(),
});

const fieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const baseFieldSchema = {
  name: z.string(),
  required: z.boolean().optional(),
};

// Campos v1: chips (single/multi), select, text, textarea.
const chipsFieldSchema = z.object({
  ...baseFieldSchema,
  type: z.literal("chips"),
  select: z.enum(["single", "multi"]).default("single"),
  options: z.array(fieldOptionSchema),
});
const selectFieldSchema = z.object({
  ...baseFieldSchema,
  type: z.literal("select"),
  options: z.array(fieldOptionSchema),
  placeholder: z.string().optional(),
});
const textFieldSchema = z.object({
  ...baseFieldSchema,
  type: z.literal("text"),
  placeholder: z.string().optional(),
  max_length: z.number().optional(),
  assist_action: assistActionSchema.optional(),
});
const textareaFieldSchema = z.object({
  ...baseFieldSchema,
  type: z.literal("textarea"),
  placeholder: z.string().optional(),
  max_length: z.number().optional(),
  assist_action: assistActionSchema.optional(),
});

const formFieldSchema = z.discriminatedUnion("type", [
  chipsFieldSchema,
  selectFieldSchema,
  textFieldSchema,
  textareaFieldSchema,
]);

const formSectionSchema = z.object({
  title: z.string(),
  hint: z.string().optional(),
  field: formFieldSchema,
});

export const formBlockSchema = z.object({
  type: z.literal("form"),
  id: z.string(),
  form_id: z.string(),
  submit_label: z.string().default("Enviar"),
  sections: z.array(formSectionSchema),
  // Erros por campo re-emitidos pelo backend na validação server-side (RFC §12.2d).
  errors: z.record(z.string()).optional(),
  fallback_text: z.string().optional(),
});

export type FormBlock = z.infer<typeof formBlockSchema>;
export type FormField = z.infer<typeof formFieldSchema>;
export type FormSection = z.infer<typeof formSectionSchema>;
export type AssistAction = z.infer<typeof assistActionSchema>;

// ── agent_hub block (T6 — Figma rodapé) ─────────────────────────────────────────
const agentActionSchema = z.object({ text: z.string(), action: z.string() });

const hubAgentSchema = z.object({
  agent_type: z.string(),
  title: z.string(),
  description: z.string().optional(),
  expanded: z.boolean().optional(),
  actions: z.array(agentActionSchema).optional(),
});

export const agentHubBlockSchema = z.object({
  type: z.literal("agent_hub"),
  id: z.string(),
  prompt: z.string().optional(),
  agents: z.array(hubAgentSchema),
  fallback_text: z.string().optional(),
});

export type AgentHubBlock = z.infer<typeof agentHubBlockSchema>;

// ── actions_bar block (T6 — Figma topo, ex.: "Exportar relatório") ──────────────
const barActionSchema = z.object({
  text: z.string(),
  action: z.string(),
  icon: z.string().optional(),
  style: z.enum(["solid", "outline", "ghost"]).optional(),
});

export const actionsBarBlockSchema = z.object({
  type: z.literal("actions_bar"),
  id: z.string(),
  placement: z.enum(["header", "inline"]).default("header"),
  actions: z.array(barActionSchema),
  fallback_text: z.string().optional(),
});

export type ActionsBarBlock = z.infer<typeof actionsBarBlockSchema>;

// ── notice block (card de aviso/callout, ex.: prazo de atendimento do chamado) ──
// Tom visual próprio (info/success/warning/danger/neutral), distinto do tone de
// negócio (positive/negative/neutral) usado em table/kpi.
const noticeToneSchema = z.enum([
  "info",
  "success",
  "warning",
  "danger",
  "neutral",
]);

export const noticeBlockSchema = z.object({
  type: z.literal("notice"),
  id: z.string(),
  text: z.string(),
  title: z.string().optional(),
  tone: noticeToneSchema.default("info"),
  icon: z.string().optional(),
  fallback_text: z.string().optional(),
});

export type NoticeBlock = z.infer<typeof noticeBlockSchema>;
export type NoticeTone = z.infer<typeof noticeToneSchema>;

// ── card_list block (lista de cartões de detalhe, ex.: chamados em aberto) ──────
const cardBadgeSchema = z.object({
  text: z.string(),
  tone: noticeToneSchema.default("neutral"),
});
const cardFieldSchema = z.object({ label: z.string(), value: z.string() });
const cardItemSchema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  badge: cardBadgeSchema.optional(),
  fields: z.array(cardFieldSchema).default([]),
});

export const cardListBlockSchema = z.object({
  type: z.literal("card_list"),
  id: z.string(),
  items: z.array(cardItemSchema),
  fallback_text: z.string().optional(),
});

export type CardListBlock = z.infer<typeof cardListBlockSchema>;

// ── schedule_form / schedule_list (agendamento de relatório no chat) ────────────
// Marcadores, não conteúdo: o backend manda apenas os dados CRUS da API de
// `/notification-schedules` e o front renderiza com os componentes já existentes da
// PED-12415 (ScheduleForm / ScheduleListItem), injetados via slots. Assim a formatação
// (frequência, próximo envio, i18n nos 3 locales) vive num lugar só, em vez de ser
// duplicada em Python e virar contrato entre repos.
//
// `schedule` / `schedules[]` são propositalmente opacos (`z.record(z.unknown())`): validar
// a shape do NotificationSchedule aqui só criaria um segundo lugar para atualizar quando a
// API mudar — quem consome já conhece o tipo. Um objeto malformado degrada no componente,
// não derruba o bloco inteiro.
export const scheduleFormBlockSchema = z.object({
  type: z.literal("schedule_form"),
  id: z.string(),
  mode: z.enum(["create", "edit"]).default("create"),
  schedule: z.record(z.unknown()).nullable().default(null),
  fallback_text: z.string().optional(),
});

export type ScheduleFormBlock = z.infer<typeof scheduleFormBlockSchema>;

export const scheduleListBlockSchema = z.object({
  type: z.literal("schedule_list"),
  id: z.string(),
  schedules: z.array(z.record(z.unknown())).default([]),
  fallback_text: z.string().optional(),
});

export type ScheduleListBlock = z.infer<typeof scheduleListBlockSchema>;

// ── discriminated union de todos os blocks conhecidos ───────────────────────────
// Novos types entram aqui como novos membros do union — sem tocar nos existentes (RFC §2.4).
export const blockSchema = z.discriminatedUnion("type", [
  chartBlockSchema,
  chipsBlockSchema,
  expandableListBlockSchema,
  kpiGroupBlockSchema,
  tableBlockSchema,
  formBlockSchema,
  agentHubBlockSchema,
  actionsBarBlockSchema,
  noticeBlockSchema,
  cardListBlockSchema,
  scheduleFormBlockSchema,
  scheduleListBlockSchema,
]);

export type Block = z.infer<typeof blockSchema>;

/**
 * Valida uma lista crua de blocks vinda do backend, descartando os inválidos/desconhecidos.
 * Retorna apenas os blocks que passaram na validação (ordem preservada).
 */
export function parseBlocks(raw: unknown): Block[] {
  if (!Array.isArray(raw)) return [];
  const out: Block[] = [];
  for (const item of raw) {
    const result = blockSchema.safeParse(item);
    if (result.success) {
      out.push(result.data);
    } else {
      // ST-1: telemetria real de block descartado (RFC §2.4) — mensurável em produção
      // via Datadog RUM, não mais só um console.warn de DEV.
      trackDroppedBlock(item, result.error.issues);
    }
  }
  return out;
}

/** Converte um `chart` block no ChartSpec plano que o <ChartRender/> consome. */
export function chartBlockToSpec(block: ChartBlock): ChartSpec {
  return {
    type: block.chart_type,
    title: block.title ?? "",
    // ChartSpec.x é string[]; normaliza números/null para string.
    x: block.x.map((v) => (v == null ? "" : String(v))),
    // ChartSpec.Series.data é number[]; troca null por 0 para o recharts.
    series: block.series.map((s) => ({
      name: s.name,
      data: s.data.map((d) => (d == null ? 0 : d)),
      unit: s.unit,
    })),
    note: block.note,
    stacked: block.options?.stacked,
    horizontal: block.options?.horizontal,
  };
}
